import React, { useState, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const ImageCropper = forwardRef((props, ref) => {
  const {
    aspectRatio = '1:1',    // Default aspect ratio
    circleMode = false,     // Enable circular crop mode
    lockAspectRatio = true, // Lock aspect ratio (non-resizable)
    acceptedFileTypes = ['image/jpeg', 'image/png', 'image/webp'],
    cropperWidth = 90,      // Width of the cropper in percentage
  } = props;
  
  // State for storing the selected image
  const [imgSrc, setImgSrc] = useState('');
  
  // State for storing crop configuration
  const [crop, setCrop] = useState();
  
  // State for completed crop
  const [completedCrop, setCompletedCrop] = useState(null);
  
  // Reference to the image element
  const imgRef = useRef(null);
  
  // Internal ref for storing cropped data
  const internalRef = useRef({
    files: []
  });
  
  // Expose the files reference to parent component
  useImperativeHandle(ref, () => internalRef.current);
  
  // Parse aspect ratio from string or number
  const getAspectRatioValue = () => {
    if (typeof aspectRatio === 'number') {
      return aspectRatio;
    }
    
    // Handle common aspect ratio strings
    const ratioMap = {
      '1:1': 1,
      '4:3': 4/3,
      '16:9': 16/9,
      '3:2': 3/2,
      '2:3': 2/3,
    };
    
    if (circleMode) {
      return 1; // Circles are always 1:1
    }
    
    return ratioMap[aspectRatio] || 1; // Default to 1:1 if invalid
  };
  
  // Function to handle file selection
  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Check if file type is allowed
      if (!acceptedFileTypes.includes(file.type)) {
        alert(`File type not supported. Please use ${acceptedFileTypes.join(', ')}`);
        return;
      }
      
      const reader = new FileReader();
      reader.addEventListener('load', () => setImgSrc(reader.result));
      reader.readAsDataURL(file);
      
      // Store original file information
      internalRef.current.originalFile = file;
    }
  };
  
  // Function to apply crop when image loads
  const onImageLoad = useCallback((img) => {
    imgRef.current = img;
    
    const { width, height } = img;
    const aspect = getAspectRatioValue();
    
    // Apply center crop with the configured aspect ratio
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: cropperWidth,
        },
        aspect,
        width,
        height
      ),
      width,
      height
    );
    
    setCrop(crop);
  }, [aspectRatio, circleMode, cropperWidth]);
  
  // Function to generate the cropped image
  const generateCroppedImage = useCallback(() => {
    if (!completedCrop || !imgRef.current) return;
    
    const canvas = document.createElement('canvas');
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    
    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;
    
    const ctx = canvas.getContext('2d');
    
    // For circle crop, create a circular clipping path
    if (circleMode) {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const radius = Math.min(centerX, centerY);
      
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      ctx.clip();
    }
    
    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY
    );
    
    // Get the original file type
    const originalFile = internalRef.current.originalFile;
    const fileType = originalFile ? originalFile.type : 'image/jpeg';
    
    // Convert canvas to blob using the original file type
    canvas.toBlob((blob) => {
      if (!blob) return;
      
      // Create a File object from the blob
      const fileName = originalFile 
        ? originalFile.name.replace(/\.[^/.]+$/, '') + '-cropped' + getFileExtension(fileType)
        : 'cropped-image' + getFileExtension(fileType);
      
      // Create a File object that mimics the structure of a file input
      const croppedFile = new File([blob], fileName, { 
        type: fileType,
        lastModified: new Date().getTime()
      });
      
      // Store the cropped file in the ref just like a FileList would
      internalRef.current.files = [croppedFile];
      
      // Display the cropped image for demonstration
      const preview = document.getElementById('preview');
      if (preview) {
        preview.src = URL.createObjectURL(blob);
      }
    }, fileType);
  }, [completedCrop, circleMode]);
  
  // Helper function to get file extension from mime type
  const getFileExtension = (mimeType) => {
    const extensions = {
      'image/jpeg': '.jpg',
      'image/png': '.png',
      'image/webp': '.webp'
    };
    return extensions[mimeType] || '.jpg';
  };
  
  // Get accepted file types for input element
  const getAcceptedFileTypesString = () => {
    return acceptedFileTypes.join(',');
  };
  
  return (
    <div className="image-cropper">
      <div className="controls">
        <input 
          type="file" 
          accept={getAcceptedFileTypesString()} 
          onChange={onSelectFile} 
        />
      </div>
      
      {!!imgSrc && (
        <ReactCrop
          crop={crop}
          onChange={(c) => setCrop(c)}
          onComplete={(c) => setCompletedCrop(c)}
          aspect={getAspectRatioValue()}
          circularCrop={circleMode}
          locked={lockAspectRatio}
        >
          <img
            src={imgSrc}
            onLoad={(e) => onImageLoad(e.currentTarget)}
            alt="Crop me"
          />
        </ReactCrop>
      )}

      {!!imgSrc && (
          <button 
            onClick={generateCroppedImage} 
            disabled={!completedCrop}
            className="crop-button"
          >
            Upload & Preview
          </button>
        )}
      
     {crop ? <div className="preview-container">
        <h3>Cropped Preview</h3>
        <img id="preview" alt="Crop preview" />
      </div> : <></> }

      
     
      
      <style jsx>{`
        .image-cropper {
          display: flex;
          flex-direction: column;
          gap: 20px;
          max-width: 800px;
          margin: 0 auto;
        }
        
        .controls {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 20px;
        }
        
        .crop-button {
          padding: 8px 16px;
          background-color: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: bold;
        }
        
        .crop-button:disabled {
          background-color: #cccccc;
          cursor: not-allowed;
        }
        
        .preview-container {
          margin-top: 20px;
        }
        
        #preview {
          max-width: 300px;
          max-height: 300px;
          border: 1px dashed #ccc;
        }
      `}</style>
    </div>
  );
});

export default ImageCropper;